import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny in-memory cache so the same avatar isn't fetched repeatedly
const cache = new Map();
// In-flight promise dedup — when 12 chat bubbles mount at once asking for
// the same avatar, we want ONE network call, not 12.
const inFlight = new Map();
// Bump key so SSE can invalidate avatars when a user changes theirs
let cacheVersion = 0;

if (typeof window !== "undefined") {
  window.__bumpAvatarCache = (uid) => {
    if (uid) { cache.delete(uid); inFlight.delete(uid); } else { cache.clear(); inFlight.clear(); }
    cacheVersion += 1;
    window.dispatchEvent(new CustomEvent("avatar:changed"));
  };
}

/**
 * Avatar — circular profile picture for a user. Falls back to colored gradient
 * with initial when the user has no picture.
 *
 *   <Avatar userId={msg.sender_id} name={msg.sender_name} size={28} authHeader={authHeader} />
 *
 * If hasPicHint is explicitly false, NO network request is made.
 */
export const Avatar = ({ userId, name, size = 32, authHeader, hasPicHint, className = "", asAdmin = false }) => {
  const [src, setSrc] = useState(null);
  const [version, setVersion] = useState(cacheVersion);

  useEffect(() => {
    const onChange = () => setVersion((v) => v + 1);
    window.addEventListener("avatar:changed", onChange);
    return () => window.removeEventListener("avatar:changed", onChange);
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (hasPicHint === false) { setSrc(null); return; }
    // Sender is the admin (CrisAngel) — special "ca-admin" placeholder card.
    if (asAdmin && userId === "admin") {
      // We try a known admin id later; for now fall back to gradient.
    }
    const cached = cache.get(userId);
    if (cached !== undefined) { setSrc(cached); return; }
    let cancelled = false;
    // Dedup: if another <Avatar/> already kicked off the request for this
    // userId, await its result instead of starting a new HTTP call.
    let pending = inFlight.get(userId);
    if (!pending) {
      pending = (async () => {
        try {
          const r = await axios.get(`${API}/users/profile-pic/${userId}`, { headers: authHeader?.() || {} });
          const url = `data:${r.data.media_mime};base64,${r.data.media_b64}`;
          cache.set(userId, url);
          return url;
        } catch {
          cache.set(userId, null);
          return null;
        } finally {
          inFlight.delete(userId);
        }
      })();
      inFlight.set(userId, pending);
    }
    pending.then((url) => { if (!cancelled) setSrc(url); });
    return () => { cancelled = true; };
  }, [userId, authHeader, hasPicHint, version, asAdmin]);

  const initial = (name || "U").trim().slice(0, 1).toUpperCase();
  const style = { width: size, height: size, fontSize: Math.max(10, size * 0.4) };

  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        data-testid={`avatar-${userId}`}
        className={`rounded-full object-cover border border-white/10 ${className}`}
        style={style}
      />
    );
  }
  return (
    <div
      data-testid={`avatar-fallback-${userId}`}
      className={`rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-700 flex items-center justify-center text-white font-bold border border-white/10 ${className}`}
      style={style}
    >
      {initial}
    </div>
  );
};
